import {GridModel} from '@xh/hoist/cmp/grid';
import {HoistModel, LoadSpec, lookup, managed, TaskObserver, XH} from '@xh/hoist/core';
import {DashViewModel} from '@xh/hoist/desktop/cmp/dash';
import {Icon} from '@xh/hoist/icon';
import {action, bindable, observable, observableRef} from '@xh/hoist/mobx';
import {compact, isEmpty} from 'lodash';
import {GridOptions} from 'ag-grid-community';
import {getDefaultOptions} from '../../../../../core/ServiceUtils';
import {PriceQuote, Security} from '../../../../data/DataTypes';
import {percentRenderer, Precision, priceRenderer} from '@xh/hoist/format';
import {NotificationMessage} from '../../../../data/MessageTypes';
import {actionCol, calcActionColWidth} from '@xh/hoist/desktop/cmp/grid';
import {placeholder} from '@xh/hoist/cmp/layout';
import {wait} from '@xh/hoist/promise';

export class QuoteWidgetModel extends HoistModel {
    apiTask = TaskObserver.trackLast();
    @bindable accessor precision: Precision = 2;
    @observableRef accessor tickers: string[] = [];
    @managed quotesGridModel: GridModel;
    @managed configGridModel = this.createConfigGridModel();
    @lookup(DashViewModel) dashViewModel: DashViewModel;
    @observable accessor isConfigOpen = false;

    override onLinked() {
        this.markPersist('tickers', {dashViewModel: this.dashViewModel});
        this.markPersist('precision', {dashViewModel: this.dashViewModel});

        this.dashViewModel.extraMenuItems.push({
            icon: Icon.gear(),
            text: 'Configure',
            actionFn: () => this.openConfig()
        });

        this.quotesGridModel = this.createQuotesGridModel();

        XH.messageHub.subscribe('price_quotes', this.xhId, (message: NotificationMessage) => {
            switch (message.envelope.eventType) {
                case 'RELOAD':
                    this.loadAsync();
                    break;
                case 'UPDATE':
                    this.updateQuote(message.payload);
                    break;
            }
        });
    }

    override async doLoadAsync(loadSpec: LoadSpec): Promise<void> {
        const tickers = compact(this.tickers);
        if (isEmpty(tickers)) return;

        const data = tickers.map(t => {
            const existing = this.quotesGridModel.store.getById(t)?.data;
            if (existing) return existing;
            else return {ticker: t};
        });

        this.quotesGridModel.clear();
        wait(50).then(() => {
            this.quotesGridModel.loadData(data);

            XH.fetchService.postJson({
                url: `${XH.getConf('svcApiUrl')}/economics/quotes/subscribe`,
                body: tickers,
                ...getDefaultOptions()
            });
        });
    }

    @action openConfig() {
        this.isConfigOpen = true;
    }

    @action closeConfig() {
        this.isConfigOpen = false;
    }

    populateConfig() {
        this.populateConfigAsync().catchDefault({alertType: 'toast'}).linkTo(this.apiTask);
    }

    private async populateConfigAsync() {
        this.configGridModel.clear();

        const securities: Security[] = [];
        for (const ticker of this.tickers) {
            const security = await XH.secMasterService.getSecurityByTickerAsync(ticker);
            securities.push(security ? security : ({id: XH.genId(), ticker, name: 'Not Found'} as unknown as Security));
        }

        this.configGridModel.loadData(securities);
    }

    addSecurity(security: Security) {
        const existing = this.configGridModel.store.records.map(rec => {
            return rec.data.id;
        });
        if (existing.includes(security.id)) return;

        this.configGridModel.store.addRecords([security]);
    }

    @action
    applyConfig() {
        const tickers: string[] = [];
        this.configGridModel.agApi.forEachNode(node => {
            tickers.push(node.data.data.ticker);
        });

        this.tickers = tickers;
        this.loadAsync();
        this.closeConfig();
    }

    get agOptions(): GridOptions {
        return {
            rowDragManaged: true,
            suppressContextMenu: true,
            preventDefaultOnContextMenu: true
        };
    }

    private updateQuote(quote: PriceQuote) {
        if (this.quotesGridModel.store.getById(quote.ticker)) this.quotesGridModel.updateData({update: [quote]});
    }

    private createQuotesGridModel(): GridModel {
        return new GridModel({
            store: {
                idSpec: 'ticker'
            },
            contextMenu: null,
            selModel: 'disabled',
            emptyText: placeholder(Icon.gear(), 'Configure'),
            hideEmptyTextBeforeLoad: false,
            columns: [
                {
                    field: 'ticker',
                    flex: 1
                },
                {
                    field: 'price',
                    width: 100,
                    align: 'right',
                    renderer: priceRenderer({precision: this.precision, prefix: '$'})
                },
                {
                    field: 'priceChange',
                    headerName: 'Px Chg',
                    width: 75,
                    align: 'right',
                    renderer: priceRenderer({
                        precision: this.precision,
                        prefix: '$',
                        colorSpec: true
                    })
                },
                {
                    field: 'priceChangePct',
                    headerName: 'Px Chg %',
                    width: 75,
                    align: 'right',
                    renderer: percentRenderer({precision: this.precision, colorSpec: true})
                }
            ]
        });
    }

    private createConfigGridModel(): GridModel {
        return new GridModel({
            hideHeaders: true,
            emptyText: 'No tickers',
            columns: [
                {field: 'id', hidden: true},
                {
                    field: 'ticker',
                    width: 105,
                    agOptions: {rowDrag: true},
                    cellClass: 'watchlist-dialog__item-ticker'
                },
                {
                    field: 'name',
                    flex: true,
                    cellClass: 'watchlist-dialog__item-name'
                },
                {
                    ...actionCol,
                    actionsShowOnHoverOnly: true,
                    width: calcActionColWidth(1),
                    actions: [
                        {
                            icon: Icon.delete(),

                            intent: 'danger',
                            actionFn: ({record}) => record.store.removeRecords(record.data.id)
                        }
                    ]
                }
            ]
        });
    }
}
